import React from 'react';
import { namedShapes, type DoodleFamily } from '../../data/doodleShapes';

// Official BCM hand-drawn accent shapes, used exactly as supplied — never
// redrawn, recolored, or distorted. Each shape ships in four brand colors, so
// the `color` prop selects the correct supplied FILE rather than tinting art.
//
// All shapes are decorative: empty alt text, hidden from screen readers, and
// never interactive.

type DoodleProps = {
  className?: string;
  /** A brand color token. Selects which supplied file is used. */
  color?: string;
};

/** Maps a brand color token to the matching supplied shape family. */
export function familyFor(color?: string): DoodleFamily {
  if (!color) return 'lightBlue';
  if (color.includes('dark-blue') || color.includes('navy')) return 'navy';
  if (color.includes('light-blue')) return 'lightBlue';
  if (color.includes('tan') || color.includes('cream')) return 'cream';
  // Yellow, and pink until BCM supplies a pink set.
  return 'yellow';
}
type ShapeKind = keyof (typeof namedShapes)['navy'];

/** Strokes read best anchored left; solid shapes stay centered. */
const leftAligned: ShapeKind[] = ['underline', 'underlineLong', 'wave', 'squiggle'];
function Shape({
  kind,
  className = '',
  color
}: DoodleProps & {
  kind: ShapeKind;
}) {
  return <img src={namedShapes[familyFor(color)][kind]} alt="" aria-hidden="true" draggable={false} decoding="async" className={`pointer-events-none select-none object-contain ${leftAligned.includes(kind) ? 'object-left' : ''} ${className}`} data-id="element-34" data-mp="35s1jf3wtk05o" />;
}
export function DoodleUnderline(props: DoodleProps) {
  return <Shape kind="underline" {...props} data-id="element-35" data-mp="2iteuxew86nik" />;
}
export function DoodleUnderlineLong(props: DoodleProps) {
  return <Shape kind="underlineLong" {...props} data-id="element-36" data-mp="2mtgw7my1texb" />;
}
export function DoodleWave(props: DoodleProps) {
  return <Shape kind="wave" {...props} data-id="element-37" data-mp="gy4h0x1e94gc" />;
}
export function DoodleSquiggle(props: DoodleProps) {
  return <Shape kind="squiggle" {...props} data-id="element-38" data-mp="kzq2h6rzur25" />;
}
export function DoodleCircle(props: DoodleProps) {
  return <Shape kind="oval" {...props} data-id="element-39" data-mp="1two7rp3bo0ax" />;
}
export function DoodleBlob(props: DoodleProps) {
  return <Shape kind="blob" {...props} data-id="element-40" data-mp="1oyczhisodxut" />;
}
export function DoodleBurst(props: DoodleProps) {
  return <Shape kind="burst" {...props} data-id="element-41" data-mp="1kym8pf35fhqa" />;
}
export function DoodleStar(props: DoodleProps) {
  return <Shape kind="star" {...props} data-id="element-42" data-mp="1g0kj0bb7wk5u" />;
}
export function DoodleSparkle(props: DoodleProps) {
  return <Shape kind="sparkle" {...props} data-id="element-43" data-mp="2coqsbbj340qg" />;
}
export function DoodleArrow(props: DoodleProps) {
  return <Shape kind="arrow" {...props} data-id="element-44" data-mp="28oyjcxps3skk" />;
}
export function DoodleHeart(props: DoodleProps) {
  return <Shape kind="heart" {...props} data-id="element-45" data-mp="23vnhzc3qk401" />;
}